/**
 * Onboarding camera loop — the review sheet that opens after every photo.
 * Type chips, how many, (supplies) restock + extras + where, (appliances) model plate, and the guest note.
 * Per the Owner Camera Onboarding v2 design.
 */
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { LEVEL_NOW_OPTIONS } from "@/lib/inventoryCatalog";
import { BUY_AT_OPTIONS, guestNoteText, placeFromName, type ReviewFields, type ReviewKind } from "@/lib/walkthroughReview";

export type ReviewTile = ReviewFields & {
  id: string;
  localUrl: string;
  track: "units" | "fullness";
  level: number | null;
  count: number;
  restock: number;
  buyAt: number;
  model: string | null;
  serial: string | null;
  stickerLocalUrl: string | null;
  stickerPhotoUrl: string | null;
  status: "reading" | "named" | "failed";
  failReason?: string;
  spotId: string | null;
};

const TYPES: { id: ReviewKind; label: string; icon: string }[] = [
  { id: "appliance", label: "Appliance", icon: "M12 22v-5M9 8V2M15 8V2M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8z" },
  {
    id: "unit",
    label: "Unit item",
    icon: "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12",
  },
  { id: "inventory", label: "Supply", icon: "M12 2.7l5.7 5.7a8 8 0 1 1-11.3 0z" },
];

function Stepper({
  label,
  sub,
  value,
  min,
  onChange,
}: {
  label: string;
  sub?: string;
  value: number;
  min: number;
  onChange: (n: number) => void;
}) {
  const btn =
    "flex h-12 w-12 items-center justify-center rounded-[14px] bg-[#f4f7f9] text-[24px] font-bold text-[#0f1720] disabled:text-[#c5d1d9]";
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[15.5px] font-bold text-[#0f1720]">{label}</div>
        {sub ? <div className="text-[12.5px] font-medium text-[#8494a2]">{sub}</div> : null}
      </div>
      <div className="flex items-center gap-2.5">
        <button type="button" aria-label={`Fewer ${label}`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))} className={btn}>
          −
        </button>
        <div className="w-9 text-center text-[22px] font-bold tabular-nums text-[#0f1720]">{value}</div>
        <button type="button" aria-label={`More ${label}`} onClick={() => onChange(value + 1)} className={btn}>
          +
        </button>
      </div>
    </div>
  );
}

function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex min-h-[48px] items-center rounded-[14px] px-4 text-[14.5px] font-bold transition-transform duration-300",
        on ? "scale-[1.03] bg-[#15776d] text-white" : "bg-[#f4f7f9] text-[#0f1720]",
      )}
      style={{ transitionTimingFunction: "cubic-bezier(.45,1.45,.45,1)" }}
    >
      {label}
    </button>
  );
}

export function ReviewSheet({
  tile,
  room,
  spotName,
  otherSpots,
  plateEditing,
  onPlateEditing,
  patch,
  onRetake,
  onRemove,
  onSave,
  onScanPlate,
}: {
  tile: ReviewTile;
  room: string;
  spotName: string | null;
  otherSpots: { id: string; name: string }[];
  plateEditing: boolean;
  onPlateEditing: (on: boolean) => void;
  patch: (p: Partial<ReviewTile>) => void;
  onRetake: () => void;
  onRemove: () => void;
  onSave: () => void;
  onScanPlate: () => void;
}) {
  const supply = tile.kind === "inventory";
  const appliance = tile.kind === "appliance";
  const level = supply && tile.track === "fullness";
  const scanned = !!(tile.model || tile.serial || tile.stickerLocalUrl || tile.stickerPhotoUrl);
  const note = guestNoteText(tile, spotName, otherSpots, room);
  const where = spotName ? "This spot" : "Here";

  const shell = "absolute inset-x-0 bottom-0 max-h-[82%] overflow-y-auto rounded-t-[26px] bg-white px-5 pb-6 pt-5";

  if (tile.status === "reading") {
    return (
      <div className={shell}>
        <div className="flex items-center gap-3.5">
          <img src={tile.localUrl} alt="" className="h-[84px] w-[84px] shrink-0 rounded-[14px] object-cover" />
          <div className="flex items-center gap-2.5 text-[18px] font-bold text-[#0f1720]">
            <Loader2 className="h-5 w-5 animate-spin text-[#24a89a]" />
            Naming this photo
          </div>
        </div>
        <div className="mt-5 flex gap-2.5">
          <button type="button" onClick={onRetake} className="flex h-[54px] flex-1 items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] text-[15.5px] font-bold">
            Retake
          </button>
          <button type="button" disabled className="flex h-[54px] flex-[1.6] items-center justify-center rounded-[14px] bg-[#e6ecf0] text-[16.5px] font-bold text-[#8494a2]">
            Save and next photo
          </button>
        </div>
      </div>
    );
  }

  if (tile.status === "failed") {
    return (
      <div className={shell}>
        <div className="flex items-center gap-3.5">
          <img src={tile.localUrl} alt="" className="h-[84px] w-[84px] shrink-0 rounded-[14px] object-cover" />
          <div className="min-w-0">
            <div className="text-[19px] font-bold text-[#0f1720]">Couldn’t read this one</div>
            <div className="mt-0.5 text-[14px] font-medium text-[#64748b]">Turn on a light and try again.</div>
          </div>
        </div>
        <div className="mt-5 flex gap-2.5">
          <button type="button" onClick={onRetake} className="flex h-[54px] flex-1 items-center justify-center rounded-[14px] bg-[#24a89a] text-[16.5px] font-bold text-white">
            Retake
          </button>
          <button
            type="button"
            onClick={() => patch({ status: "named", name: "" })}
            className="flex h-[54px] flex-1 items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] text-[15.5px] font-bold"
          >
            Keep anyway
          </button>
        </div>
        <button type="button" onClick={onRemove} className="mt-2 flex h-11 w-full items-center justify-center text-[15px] font-bold text-[#b4453c]">
          Remove
        </button>
      </div>
    );
  }

  return (
    <div className={shell}>
      <div className="flex gap-3.5">
        <img src={tile.localUrl} alt="" className="h-[84px] w-[84px] shrink-0 rounded-[14px] object-cover" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold tracking-[0.8px] text-[#15776d]">
            {room.toUpperCase()} · {(spotName ?? "In the open").toUpperCase()}
          </div>
          <input
            value={tile.name}
            onChange={(e) => patch({ name: e.target.value })}
            placeholder="Name it"
            aria-label="Item name"
            style={{ fontSize: 17 }}
            className="mt-1 h-[48px] w-full rounded-[14px] border-[1.5px] border-[#e6edf1] bg-[#f9fbfc] px-3.5 font-bold outline-none focus:border-[#24a89a]"
          />
        </div>
      </div>

      <div className="mt-4 flex gap-2.5">
        {TYPES.map((t) => {
          const on = tile.kind === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() =>
                patch(
                  t.id === "inventory" && tile.kind !== "inventory"
                    ? { kind: "inventory", track: "units" }
                    : { kind: t.id },
                )
              }
              className={cn(
                "flex h-16 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-[16px] text-[13.5px] font-bold transition-transform duration-300",
                on ? "scale-[1.04] bg-[#15776d] text-white" : "bg-[#f4f7f9] text-[#0f1720]",
              )}
              style={{ transitionTimingFunction: "cubic-bezier(.45,1.45,.45,1)" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d={t.icon} />
              </svg>
              {t.label}
            </button>
          );
        })}
      </div>

      {appliance ? (
        <div className="mt-4 rounded-[18px] bg-[#f4f7f9] p-3.5">
          {!scanned ? (
            <div className="flex gap-2.5">
              <button type="button" onClick={onScanPlate} className="flex h-[52px] flex-[1.5] items-center justify-center rounded-[14px] bg-[#15776d] text-[15.5px] font-bold text-white">
                Scan model plate
              </button>
              <button type="button" onClick={onSave} className="flex h-[52px] flex-1 items-center justify-center rounded-[14px] bg-white text-[15.5px] font-bold text-[#64748b]">
                Skip
              </button>
            </div>
          ) : plateEditing ? (
            <div className="flex flex-col gap-2.5">
              {(["model", "serial"] as const).map((f) => (
                <label key={f} className="flex flex-col gap-1">
                  <span className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">{f.toUpperCase()}</span>
                  <input
                    value={tile[f] ?? ""}
                    placeholder="Not read yet"
                    onChange={(e) => patch({ [f]: e.target.value || null })}
                    style={{ fontSize: 16 }}
                    className="h-12 rounded-xl border border-[#e6edf1] bg-white px-3.5 font-medium outline-none"
                  />
                </label>
              ))}
              <button type="button" onClick={() => onPlateEditing(false)} className="h-11 self-end rounded-[12px] bg-[#15776d] px-5 text-[15px] font-bold text-white">
                Done
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex min-w-0 flex-1 gap-5">
                <div className="min-w-0">
                  <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">MODEL</div>
                  <div className="truncate text-[15.5px] font-bold text-[#0f1720]">{tile.model || "Not read"}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">SERIAL</div>
                  <div className="truncate text-[15.5px] font-bold text-[#0f1720]">{tile.serial || "Not read"}</div>
                </div>
              </div>
              <button type="button" onClick={() => onPlateEditing(true)} className="text-[14.5px] font-bold text-[#15776d]">
                Edit
              </button>
              <button type="button" onClick={onScanPlate} className="text-[14.5px] font-bold text-[#15776d]">
                Rescan
              </button>
            </div>
          )}
        </div>
      ) : null}

      {supply ? (
        <div className="mt-4 flex flex-col gap-2">
          <div className="text-[13px] font-bold text-[#64748b]">How do you track it?</div>
          <div className="flex rounded-[14px] bg-[#f1f5f7] p-1">
            {(["units", "fullness"] as const).map((tr) => (
              <button
                key={tr}
                type="button"
                onClick={() => patch({ track: tr })}
                className={cn(
                  "flex h-11 flex-1 items-center justify-center rounded-[11px] text-[15px]",
                  tile.track === tr || (tr === "units" && tile.track !== "fullness")
                    ? "bg-white font-bold text-[#0f1720] shadow-[0_1px_3px_rgba(15,23,32,0.12)]"
                    : "font-medium text-[#64748b]",
                )}
              >
                {tr === "units" ? "Count" : "Level"}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-3 flex flex-col gap-4">
        {level ? (
          <>
            <div className="flex gap-1.5">
              {LEVEL_NOW_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => patch({ level: opt.value })}
                  className={cn(
                    "flex h-[52px] flex-1 items-center justify-center rounded-[12px] text-[15px] font-bold",
                    tile.level === opt.value ? "bg-[#15776d] text-white" : "bg-[#f4f7f9] text-[#0f1720]",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div>
              <div className="mb-1.5 text-[15.5px] font-bold text-[#0f1720]">Restock at</div>
              <div className="flex flex-wrap gap-2">
                {BUY_AT_OPTIONS.map((o) => (
                  <Chip key={o.label} label={`Buy at ${o.label}`} on={tile.buyAt === o.value} onClick={() => patch({ buyAt: o.value })} />
                ))}
              </div>
            </div>
          </>
        ) : tile.kind !== "appliance" ? (
          <>
            <Stepper label="How many" value={tile.count} min={1} onChange={(n) => patch({ count: n })} />
            {supply ? (
              <Stepper
                label="Restock at"
                sub="Buy more when this many are left"
                value={tile.restock}
                min={0}
                onChange={(n) => patch({ restock: n })}
              />
            ) : null}
          </>
        ) : null}

        {supply ? (
          <div className="flex flex-col gap-3">
            <div className="text-[15.5px] font-bold text-[#0f1720]">Extras for guests</div>
            <div className="flex rounded-[14px] bg-[#f1f5f7] p-1">
              {(
                [
                  { id: "none", label: "Skip" },
                  { id: "fixed", label: "Every stay" },
                  { id: "per_guests", label: "By guests" },
                ] as const
              ).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() =>
                    patch({
                      extrasMode: m.id,
                      ...(m.id === "per_guests" && tile.extrasRows.length === 0
                        ? { extrasRows: [{ id: "r1", guests: 2, qty: 2 }] }
                        : {}),
                    })
                  }
                  className={cn(
                    "flex h-11 flex-1 items-center justify-center rounded-[11px] text-[14.5px]",
                    tile.extrasMode === m.id
                      ? "bg-white font-bold text-[#0f1720] shadow-[0_1px_3px_rgba(15,23,32,0.12)]"
                      : "font-medium text-[#64748b]",
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {tile.extrasMode === "fixed" ? (
              <Stepper label="Leave out" value={tile.extrasQty} min={1} onChange={(n) => patch({ extrasQty: n })} />
            ) : null}

            {tile.extrasMode === "per_guests" ? (
              <div className="flex flex-col gap-3">
                {tile.extrasRows.map((r) => (
                  <div key={r.id} className="rounded-[16px] bg-[#f4f7f9] p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-[12px] font-bold tracking-[0.6px] text-[#8494a2]">IF THE PARTY IS</div>
                      {tile.extrasRows.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => patch({ extrasRows: tile.extrasRows.filter((x) => x.id !== r.id) })}
                          className="text-[13px] font-bold text-[#b4453c]"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                    <div className="mt-2 flex flex-col gap-2.5">
                      <Stepper
                        label="Guests"
                        value={r.guests}
                        min={1}
                        onChange={(n) =>
                          patch({ extrasRows: tile.extrasRows.map((x) => (x.id === r.id ? { ...x, guests: n } : x)) })
                        }
                      />
                      <Stepper
                        label="Leave out"
                        value={r.qty}
                        min={1}
                        onChange={(n) =>
                          patch({ extrasRows: tile.extrasRows.map((x) => (x.id === r.id ? { ...x, qty: n } : x)) })
                        }
                      />
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const last = tile.extrasRows[tile.extrasRows.length - 1];
                    patch({
                      extrasRows: [
                        ...tile.extrasRows,
                        { id: `r${Date.now()}`, guests: (last?.guests ?? 2) + 2, qty: (last?.qty ?? 2) + 2 },
                      ],
                    });
                  }}
                  className="flex h-12 items-center justify-center rounded-[14px] border-[1.5px] border-dashed border-[#bfe3dd] text-[15px] font-bold text-[#15776d]"
                >
                  + Add another party size
                </button>
              </div>
            ) : null}

            {tile.extrasMode !== "none" ? (
              <div>
                <div className="mb-1.5 text-[15.5px] font-bold text-[#0f1720]">Where do you leave them?</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: "spot", label: where },
                    ...otherSpots.slice(0, 3).map((x) => ({ id: x.id, label: x.name })),
                    { id: "custom", label: "Other place" },
                  ].map((c) => {
                    const on = (tile.extrasPlaces.length ? tile.extrasPlaces : ["spot"]).includes(c.id);
                    return (
                      <Chip
                        key={c.id}
                        label={c.label}
                        on={on}
                        onClick={() => {
                          const cur = tile.extrasPlaces.length ? tile.extrasPlaces : ["spot"];
                          const next = on ? cur.filter((x) => x !== c.id) : [...cur, c.id];
                          patch({ extrasPlaces: next.length ? next : ["spot"] });
                        }}
                      />
                    );
                  })}
                </div>
                {tile.extrasPlaces.includes("custom") ? (
                  <input
                    value={tile.extrasCustom}
                    onChange={(e) => patch({ extrasCustom: e.target.value })}
                    placeholder="Hall closet"
                    aria-label="Where extras go"
                    style={{ fontSize: 16 }}
                    className="mt-2.5 h-[50px] w-full rounded-[13px] border-[1.5px] border-[#e6edf1] bg-[#f8fafb] px-4 font-medium outline-none focus:border-[#24a89a]"
                  />
                ) : null}
                {tile.extrasPlaces.length > 1 ? (
                  <div className="mt-1.5 text-[12.5px] font-medium text-[#8494a2]">The amount is split across these places.</div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {(supply && tile.extrasMode !== "none") || appliance ? (
        <div className="mt-4 rounded-[18px] bg-[#f4f7f9] p-3.5">
          <button
            type="button"
            onClick={() => patch({ guestOn: !tile.guestOn })}
            className="flex w-full items-center justify-between gap-3 text-left"
            aria-pressed={tile.guestOn}
          >
            <span className="text-[15.5px] font-bold text-[#0f1720]">Tell guests where it is</span>
            <span className={cn("flex h-8 w-14 shrink-0 items-center rounded-full p-1 transition-colors", tile.guestOn ? "bg-[#15776d]" : "bg-[#cdd8df]")}>
              <span className={cn("h-6 w-6 rounded-full bg-white transition-transform", tile.guestOn ? "translate-x-6" : "translate-x-0")} />
            </span>
          </button>
          {note ? (
            <div className="mt-3">
              <div className="text-[10.5px] font-bold tracking-[0.9px] text-[#8494a2]">GUESTS SEE</div>
              <div className="mt-0.5 text-[15px] font-semibold text-[#0f1720]">{note}</div>
            </div>
          ) : null}
          {appliance && tile.guestOn ? (
            <input
              value={tile.applPlace ?? ""}
              onChange={(e) => patch({ applPlace: e.target.value || null })}
              placeholder={placeFromName(spotName || room)}
              aria-label="Where the appliance is"
              style={{ fontSize: 16 }}
              className="mt-3 h-[46px] w-full rounded-[12px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 font-medium outline-none focus:border-[#24a89a]"
            />
          ) : null}
        </div>
      ) : null}

      <div className="mt-5 flex gap-2.5">
        <button type="button" onClick={onRetake} className="flex h-[54px] flex-1 items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] text-[15.5px] font-bold">
          Retake
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!tile.name.trim()}
          className={cn(
            "flex h-[54px] flex-[1.6] items-center justify-center rounded-[14px] text-[16.5px] font-bold",
            tile.name.trim() ? "bg-[#15776d] text-white shadow-[0_8px_20px_rgba(21,119,109,0.26)]" : "bg-[#e6ecf0] text-[#8494a2]",
          )}
        >
          Save and next photo
        </button>
      </div>
      <button type="button" onClick={onRemove} className="mt-1.5 flex h-11 w-full items-center justify-center text-[14.5px] font-bold text-[#b4453c]">
        Remove
      </button>
    </div>
  );
}
